import { NotificationCourse } from "../types/models/notification-course";
import { transformDatetime } from "./transformDatetime";
import { transformPrice } from "./transform-price";

// Habillage d'une notification de course. Le backend pousse des données brutes
// — client, trajet, statut — et laisse la formulation au client : c'est ici que
// se décide ce qu'un gestionnaire lit dans son toast.

/**
 * Libellés traduits pour {@link titreNotificationCourse} et
 * {@link detailNotificationCourse}. Fonctions utilitaires appelées hors du
 * rendu React (pas de hook possible ici) : les traductions sont donc résolues
 * par l'appelant via `useTranslations` et passées en argument.
 */
export type NotificationCourseLabels = {
  newRide: (id: number) => string;
  rideModified: (id: number) => string;
  rideAssigned: (id: number) => string;
  rideStatus: (id: number, statut: string) => string;
  priceLine: (from: string, to: string) => string;
  driverFallback: string;
  statusChanged: string;
  statutLabels: Record<number, string>;
};

const libelleStatut = (
  statut: number | null,
  labels: NotificationCourseLabels,
) =>
  statut === null
    ? labels.statusChanged
    : (labels.statutLabels[statut] ?? labels.statusChanged);

/**
 * « Prix : 100,00 € → 145,00 € », ou rien si le tarif n'a pas bougé. Composé ici
 * et non côté serveur : la mise en forme monétaire suit la culture du
 * navigateur, pas celle du conteneur qui fait tourner l'API.
 */
const ecartDePrix = (
  notification: NotificationCourse,
  labels: NotificationCourseLabels,
) =>
  notification.prixPrecedent === null
    ? null
    : labels.priceLine(
        transformPrice(notification.prixPrecedent),
        transformPrice(notification.prix),
      );

/** Ligne de titre : ce qui vient de se passer. */
export const titreNotificationCourse = (
  notification: NotificationCourse,
  labels: NotificationCourseLabels,
) => {
  switch (notification.type) {
    case "Creation":
      return labels.newRide(notification.idCourse);

    case "Modification":
      return labels.rideModified(notification.idCourse);

    case "Affectation":
      return labels.rideAssigned(notification.idCourse);

    default:
      return labels.rideStatus(
        notification.idCourse,
        libelleStatut(notification.statut, labels),
      );
  }
};

/**
 * Ligne de détail. Le toast n'en montre que deux lignes : on y met ce qui
 * permet de décider s'il faut ouvrir la fiche, pas tout ce qu'on sait.
 */
export const detailNotificationCourse = (
  notification: NotificationCourse,
  labels: NotificationCourseLabels,
  locale = "fr-BE",
) => {
  const quand = transformDatetime(notification.dateCourse, locale);

  switch (notification.type) {
    case "Creation":
      return [
        notification.client,
        quand,
        notification.trajet,
        transformPrice(notification.prix),
      ]
        .filter(Boolean)
        .join(" • ");

    case "Modification":
      // L'écart de prix passe devant : c'est ce qu'un gestionnaire regarde en
      // premier sur une course qu'il connaît déjà.
      return [notification.client, quand, ecartDePrix(notification, labels)]
        .filter(Boolean)
        .join(" • ");

    case "Affectation":
      return [
        notification.chauffeur ?? labels.driverFallback,
        notification.client,
        quand,
      ]
        .filter(Boolean)
        .join(" • ");

    default:
      return [notification.client, quand, notification.commentaire]
        .filter(Boolean)
        .join(" • ");
  }
};
